/**
 * <hotel-package-list> 顶层 React App(标题 + 日期栏 + 营销横幅 + 内联列表)
 *
 * 用共享控制器 + DateBar(纯日期选择器)+ PackageList(常驻)。
 * 点套餐 handleBook:window.open 提单页 + 派发 book(带 packageId)。
 * 房券列表(VoucherList)置于领红包下方、套餐列表上方:两营销模块聚顶,
 * DateBar + PackageList 作为日期驱动的预订单元保持相邻。共享逻辑见 shared.ts。
 */
import { useEffect } from 'react';
import { toLocalDateString, formatYMDSlash } from '../lib/date';
import type { BookPayload } from '../types';
import type { PackageItem } from '@/types/room';
import { useDateBarController, openUrl, dispatch, type AppProps } from './shared';
import { DateBar } from './DateBar';
import { PackageList } from './PackageList';
import { CouponBanner } from './CouponBanner';
import { SdkLoginModal } from '../lib/SdkLoginModal';
import { VoucherList } from './VoucherList';
import { point } from '../statistic';

/** 埋点 pageName:酒店官网首页-订房模块(本模块所有事件统一用此名) */
const PAGE_NAME = '酒店官网首页-订房模块';

export function PackageListApp({ initialCheckIn, initialCheckOut, limit, host }: AppProps) {
  const { hotel, dates, handleDateChange } = useDateBarController(
    host,
    initialCheckIn,
    initialCheckOut
  );

  // 访问埋点:模块挂载即上报一次
  useEffect(() => {
    point({ page: PAGE_NAME, action: '访问' });
  }, []);

  const handleBook = (pkg: PackageItem) => {
    openUrl('/booking', {
      packageId: String(pkg.id),
      ...(pkg.packageType != null ? { packageType: String(pkg.packageType) } : {}),
      checkIn: toLocalDateString(dates.checkIn),
      checkOut: toLocalDateString(dates.checkOut),
    });
    const payload: BookPayload = {
      packageId: pkg.id,
      ...(pkg.packageType != null ? { packageType: pkg.packageType } : {}),
      checkIn: toLocalDateString(dates.checkIn),
      checkOut: toLocalDateString(dates.checkOut),
    };
    dispatch(host, 'book', payload);
  };

  const handleViewMore = () => {
    point({ page: PAGE_NAME, action: '查看更多房型' });
    openUrl('/packages', {
      checkIn: toLocalDateString(dates.checkIn),
      checkOut: toLocalDateString(dates.checkOut),
    });
  };

  // 切换日期埋点:包一层,仅 <hotel-package-list> 上报(不污染 date-bar/book-bar 共用控制器)
  const handleDateChangeTracked = (checkIn: Date, checkOut: Date) => {
    handleDateChange(checkIn, checkOut);
    point({
      page: PAGE_NAME,
      action: '切换日期',
      actionValue: `${formatYMDSlash(checkIn)}~${formatYMDSlash(checkOut)}`,
    });
  };

  // loading/denied 均不渲染(宿主 hidden 已占位处理由控制器 effect 完成)
  if (hotel !== 'ok') return null;

  return (
    <div className="hh-root">
      <CouponBanner />
      {/* SDK 登录弹窗:portal 到 body 级独立 shadow,由 login-manager.openLogin 驱动
          (领红包未登录续接 / 未来预定续接接口预留) */}
      <SdkLoginModal />

      {/* 特价预售房券列表:领红包下方、套餐列表上方(两营销模块聚顶,DateBar+套餐列表为日期驱动单元) */}
      <VoucherList pageName={PAGE_NAME} />

      <header className="hh-header">
        <h1 className="hh-header__title">房间预订</h1>
        <DateBar
          checkIn={dates.checkIn}
          checkOut={dates.checkOut}
          onDateChange={handleDateChangeTracked}
        />
      </header>

      <PackageList
        checkIn={dates.checkIn}
        checkOut={dates.checkOut}
        limit={limit}
        onBook={handleBook}
        onViewMore={handleViewMore}
      />
    </div>
  );
}
